
import { Package, Store, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MolecularBackground } from "@/components/ui/molecular-background";
import { HeartbeatAnimation } from "@/components/ui/heartbeat-animation";
import { useState } from "react";

interface WelcomeBannerProps {
  onTabChange?: (tab: string) => void;
}

export const WelcomeBanner = ({
  onTabChange
}: WelcomeBannerProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const handleTabChange = (tab: string) => {
    console.log('WelcomeBanner - Changing tab to:', tab);
    onTabChange?.(tab);
  };

  return (
    <div className="relative rounded-2xl overflow-hidden min-h-[400px] mb-4 sm:mb-6 medical-texture">
      <div className="absolute inset-0 bg-transparent z-0" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#ffffff]/90 via-[#ffffff]/80 to-transparent z-10" />
      
      {/* Molecular background */}
      <MolecularBackground count={15} className="opacity-30" />
      
      {/* Heartbeat pulse animation */}
      <HeartbeatAnimation count={2} className="opacity-30" />
      
      <div className="grid grid-cols-1 md:grid-cols-[1.2fr,1fr] h-full relative">
        {/* Content */}
        <div className="relative z-20 h-full flex flex-col justify-center p-6 sm:p-8 lg:p-12">
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl font-bold leading-tight bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent flex items-center gap-3">
                Services
                <HeartPulse className={`h-8 w-8 text-blue-600 transition-transform duration-500 ${isHovered ? 'scale-125' : 'scale-100'}`} />
                <span className="block mt-2">
                  Pharmaceutiques
                </span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-[600px] leading-relaxed">
                Découvrez notre gamme complète de services de santé et produits pharmaceutiques. 
                Des solutions professionnelles adaptées à vos besoins, dispensées par des experts qualifiés.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="group relative overflow-hidden bg-medical"
                onClick={() => handleTabChange('services')}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Store className="h-5 w-5 mr-2 relative z-10 group-hover:text-white transition-colors" />
                <span className="relative z-10 group-hover:text-white transition-colors">Services de Santé</span>
                <span className="absolute inset-0 bg-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="gap-2 fill-button group hover:border-blue-300 transition-colors duration-300" 
                onClick={() => handleTabChange('products')}
              >
                <Package className="h-5 w-5 group-hover:text-blue-600 transition-colors" />
                <span>Produits Pharmaceutiques</span>
                <span className="icon ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">💊</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Image Container */}
        <div className="relative hidden md:block overflow-hidden -ml-60">
          <img 
            alt="Services pharmaceutiques professionnels" 
            src="/lovable-uploads/4a43b3ee-2efe-45c0-a53f-978447eb1bb7.png" 
            className="absolute w-full h-full object-cover object-center"
          />
          <div className="absolute -top-6 -right-6 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg animate-bounce border border-blue-100">
            <p className="text-sm font-medium text-blue-700">Nouveau</p>
            <p className="text-xs text-muted-foreground">Service Premium</p>
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg animate-pulse border border-blue-100">
            <p className="text-sm font-medium text-blue-700">Sécurisé</p>
            <p className="text-xs text-muted-foreground">Service Vérifié</p>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#f8fafc] rounded-full transform translate-x-1/2 translate-y-1/2 opacity-50" />
      <div className="absolute -top-32 -left-32 w-64 h-64 bg-[#f1f5f9] rounded-full opacity-50" />
    </div>
  );
};
